import React, { useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { theme, VStack } from '@sg/garnish';
import type { Address } from '@sg/graphql-schema';

import { useLocalizationContext } from '@order/Localization';

import { AccountScreenBorderedSection } from '../Account';
import { AddressListItem } from './AddressListItem';

export type AddressListProps = Readonly<{
  items: readonly Address[];
  onSelectItem: (addressId: string) => void;
  onSelectAdd: () => void;
}> &
  ViewProps;

export const AddressList = ({
  items,
  onSelectItem,
  onSelectAdd,
}: AddressListProps): React.ReactElement => {
  const { t } = useLocalizationContext();

  const getTitle = useCallback(({ name, street }: Address): string => {
    if (name) return name;

    if (street) return street;

    return '';
  }, []);

  const getSubtitle = useCallback(
    ({ street, secondaryStreet, city, state, zipCode }: Address): string => {
      return [
        street,
        secondaryStreet,
        [city, [state, zipCode].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', '),
      ]
        .filter(Boolean)
        .join('\n');
    },
    [],
  );

  return (
    <AccountScreenBorderedSection>
      <VStack gap={0} hasDivider={true} dividerColor={theme.colors.LIGHT}>
        {items.map((address) => (
          <AddressListItem
            key={address.id}
            testID={`addresses.${address.name}-card-pressable`}
            accessibilityLabel={t('addresses.address-label', {
              address: address.name,
            })}
            iconName={
              address.name
                ? address.name === 'home'
                  ? 'IconHomeStrokeSm'
                  : address.name === 'work'
                    ? 'IconWorkStrokeSm'
                    : 'IconCustomStrokeSm'
                : 'IconPickupStrokeSm'
            }
            title={getTitle(address)}
            subtitle={getSubtitle(address)}
            onPress={() => {
              onSelectItem(address.id);
            }}
          />
        ))}
        <AddressListItem
          testID="account.edit-address.add"
          accessibilityLabel={t('addresses.add-new')}
          accessibilityHint={t('addresses.title')}
          iconName="IconAdd"
          title={t('addresses.add-new')}
          onPress={onSelectAdd}
        />
      </VStack>
    </AccountScreenBorderedSection>
  );
};
